import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import { getUserInfo } from "../store/rootReducer";
import { useEngagementTheme, engagementPageSx, engagementButtonSx, engagementSelectSx, EngagementPill } from "../components/engagementTheme";

const PERIODS = [
  { value: "30", label: "Last 30 days" },
  { value: "quarter", label: "This quarter" },
  { value: "90", label: "Last 90 days" },
];

const Tile = ({ t, label, value, note }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", padding: "16px 18px", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px" }}>
    <Typography sx={{ fontSize: 14, color: t.textMuted }}>{label}</Typography>
    <Typography sx={{ fontSize: 26, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    <Typography sx={{ fontSize: 13, color: t.textSecondary }}>{note}</Typography>
  </Box>
);

const HealthCell = ({ t, health }) => {
  if (!health.total) return <Typography sx={{ fontSize: 13, color: t.textMuted }}>No scored clinics</Typography>;
  const pct = (n) => (health.total > 0 ? (n / health.total) * 100 : 0);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 160 }}>
      <Typography sx={{ fontWeight: 600 }}>Average {health.average}</Typography>
      <Box role="img" aria-label={`${health.healthyCount} healthy, ${health.watchCount} watch, ${health.riskCount} at risk`} sx={{ display: "flex", height: 10, borderRadius: "5px", overflow: "hidden" }}>
        <Box sx={{ width: `${pct(health.healthyCount)}%`, bgcolor: t.tone.ok.color }} />
        <Box sx={{ width: `${pct(health.watchCount)}%`, bgcolor: t.tone.warn.color }} />
        <Box sx={{ width: `${pct(health.riskCount)}%`, bgcolor: t.tone.crit.color }} />
      </Box>
      <Typography sx={{ fontSize: 13, color: t.textMuted }}>{health.healthyCount} healthy, {health.watchCount} watch, {health.riskCount} at risk</Typography>
    </Box>
  );
};

const EngagementTeamOverview = () => {
  const t = useEngagementTheme();
  const [data, setData] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [period, setPeriod] = useState("30");
  const [atRiskPage, setAtRiskPage] = useState(1);
  const [planChangesPage, setPlanChangesPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openReassignId, setOpenReassignId] = useState("");
  const [reassignTarget, setReassignTarget] = useState("");
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const userInfo = useSelector(getUserInfo);
  const userRoles = Array.isArray(userInfo?.role) ? userInfo.role : [userInfo?.role].filter(Boolean);
  const isAdmin = userRoles.includes("admin");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/officers`, { headers: { Authorization: `Bearer ${auth}` } })
      .then((res) => {
        if (!cancelled) setOfficers(res.data.officers || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    return axios
      .get(`${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/team-overview`, {
        params: { period, atRiskPage, planChangesPage },
        headers: { Authorization: `Bearer ${auth}` },
      })
      .then((res) => setData(res.data))
      .catch(() => Toast.error("Error while fetching team overview"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, atRiskPage, planChangesPage]);

  const reassign = async (doctorId) => {
    if (!reassignTarget) return;
    setSaving(true);
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/assignments/${doctorId}`,
        { ownerId: reassignTarget },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      Toast.success("Clinic reassigned.");
      setOpenReassignId("");
      setReassignTarget("");
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not reassign this clinic.");
    } finally {
      setSaving(false);
    }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/assignments/backfill`,
        {},
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      Toast.success(
        res.data.assigned > 0
          ? `Assigned ${res.data.assigned} of ${res.data.consideredUnassigned} unassigned clinics.`
          : "Nothing to backfill — every clinic already has an owner."
      );
      load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not run the backfill.");
    } finally {
      setBackfilling(false);
    }
  };

  const selectSx = { ...engagementSelectSx(t), minWidth: 180 };

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em" }}>Team overview</Typography>
          <Typography sx={{ fontSize: 15, color: t.textMuted }}>
            Engagement team performance. Region grouping isn't tracked (no region field exists), so that isn't shown.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontSize: 13, color: t.textMuted }}>Period</Typography>
            <Select size="small" value={period} onChange={(e) => { setPeriod(e.target.value); setAtRiskPage(1); setPlanChangesPage(1); }} sx={selectSx}>
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </Box>
          {isAdmin && (
            <Box
              component="button"
              type="button"
              disabled={backfilling}
              onClick={runBackfill}
              sx={{
                ...engagementButtonSx(t),
                cursor: backfilling ? "default" : "pointer",
                opacity: backfilling ? 0.6 : 1,
              }}
            >
              {backfilling ? "Backfilling…" : "Backfill unassigned clinics"}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ position: "relative", minHeight: 80 }}>
        {loading && <PageLoader />}
        {data && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" }}>
              <Tile t={t} label="Renewal rate" value={data.kpis.renewalRate === null ? "—" : `${data.kpis.renewalRate}%`} note="Renewed of renewed+churned this month" />
              <Tile t={t} label="Active clinics" value={data.kpis.activeClinicsPct === null ? "—" : `${data.kpis.activeClinicsPct}%`} note="Scanned at least once in the last 14 days" />
              <Tile t={t} label="NPS" value={data.kpis.nps.average === null ? "—" : data.kpis.nps.average} note={`${data.kpis.nps.responsePct}% of clinics have a logged score`} />
              <Tile t={t} label="Contact coverage" value={data.kpis.contactCoveragePct === null ? "—" : `${data.kpis.contactCoveragePct}%`} note="Spoken to within their tier limit" />
              <Tile t={t} label="Ending in 90 days" value={data.kpis.endingIn90} note="Across the whole team" />
              <Tile t={t} label="Overdue promises" value={data.kpis.totalOverduePromises} note={`${data.kpis.totalOpenPromises} open in total`} />
            </Box>

            <Box sx={{ bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px", overflowX: "auto" }}>
              <Box sx={{ padding: "18px 20px 6px" }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Engagement officers</Typography>
              </Box>
              <Box component="table" sx={{ width: "100%", minWidth: 900, borderCollapse: "collapse", fontSize: 14 }}>
                <Box component="thead">
                  <Box component="tr" sx={{ textAlign: "left", color: t.textMuted, fontSize: 13 }}>
                    {["Officer", "Book", "Clinic health", "Calls, spoke of attempted", "Contact coverage", "Tasks on time", "Open promises", "Overdue promises", "Renewed of due", "Partner intros to camps", "NPS"].map((h) => (
                      <Box component="th" key={h} sx={{ padding: "10px 12px", borderBottom: `1px solid ${t.border}`, fontWeight: 600 }}>{h}</Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {data.officers.map((o) => (
                    <Box component="tr" key={o.id} sx={{ verticalAlign: "top" }}>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600 }}>{o.name}</Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{o.bookSize} clinics</Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}><HealthCell t={t} health={o.health} /></Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontVariantNumeric: "tabular-nums" }}>{o.callsSpoke} of {o.callsAttempted}</Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600 }}>{o.contactCoveragePct === null ? "—" : `${o.contactCoveragePct}%`}</Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600 }}>
                        {o.sla.pct === null ? "—" : `${o.sla.pct}%`}
                        {o.sla.total > 0 && <Typography sx={{ fontSize: 13, color: t.textMuted, fontWeight: 400 }}>{o.sla.onTime} of {o.sla.total}</Typography>}
                      </Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{o.openPromises}</Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, color: o.overduePromises > 0 ? t.txtTone.crit : "inherit" }}>{o.overduePromises}</Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontVariantNumeric: "tabular-nums" }}>{o.renewedOfDue}</Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                        {o.partnerIntros.findingPartners + o.partnerIntros.introOrTalks} intros, {o.partnerIntros.campsScheduled} camps
                      </Box>
                      <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          <Typography sx={{ fontWeight: 700 }}>{o.nps.average === null ? "—" : o.nps.average}</Typography>
                          <Typography sx={{ fontSize: 13, color: t.textMuted }}>{o.nps.responsePct}% have a score</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "flex-start" }}>
              <Box sx={{ flex: "999 1 560px", minWidth: 0, bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px", padding: "18px 20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Renewals at risk across the team</Typography>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Ending within 90 days with health below 50, or unpaid use.</Typography>
                {data.atRisk.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted }}>Nothing at risk right now.</Typography>}
                {data.atRisk.map((r) => (
                  <Box key={r.id} sx={{ display: "flex", flexDirection: "column", gap: "10px", pt: "12px", borderTop: `1px solid ${t.borderMuted}` }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px" }}>
                      <Box sx={{ flex: "1 1 240px", minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{r.doctorName}</Typography>
                        <Typography sx={{ fontSize: 13, color: t.textMuted }}>{r.clinicName}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, color: t.textSecondary, width: 160 }}>{r.why}</Typography>
                      <Typography sx={{ fontSize: 13, color: t.textSecondary, width: 140 }}>{r.ownerName || "Unassigned"}</Typography>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => {
                          setOpenReassignId(openReassignId === r.id ? "" : r.id);
                          setReassignTarget("");
                        }}
                        sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}
                      >
                        Reassign
                      </Box>
                    </Box>
                    {openReassignId === r.id && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "flex-end", padding: "12px", bgcolor: t.hoverBg, borderRadius: "10px" }}>
                        <Select size="small" value={reassignTarget} displayEmpty onChange={(e) => setReassignTarget(e.target.value)} sx={selectSx}>
                          <MenuItem value="" disabled>New owner</MenuItem>
                          {officers.filter((o) => o._id !== r.ownerId).map((o) => (
                            <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                          ))}
                        </Select>
                        <Box
                          component="button"
                          type="button"
                          disabled={!reassignTarget || saving}
                          onClick={() => reassign(r.id)}
                          sx={{ ...engagementButtonSx(t), cursor: reassignTarget ? "pointer" : "default", opacity: reassignTarget ? 1 : 0.6 }}
                        >
                          Confirm
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
                {(data.atRiskTotalPages || 1) > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", paddingTop: "10px" }}>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {data.atRiskPage || 1} of {data.atRiskTotalPages || 1}</Typography>
                    <Box sx={{ display: "flex", gap: "8px" }}>
                      <Box
                        component="button"
                        type="button"
                        disabled={atRiskPage <= 1}
                        onClick={() => setAtRiskPage((p) => Math.max(1, p - 1))}
                        sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: atRiskPage <= 1 ? "not-allowed" : "pointer" }}
                      >
                        Previous
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        disabled={atRiskPage >= (data.atRiskTotalPages || 1)}
                        onClick={() => setAtRiskPage((p) => Math.min(data.atRiskTotalPages || 1, p + 1))}
                        sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: atRiskPage >= (data.atRiskTotalPages || 1) ? "not-allowed" : "pointer" }}
                      >
                        Next
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ flex: "1 1 320px", minWidth: 0, bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px", padding: "18px 20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Plan changes at renewal</Typography>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>This period. Yearly value change against the old plan.</Typography>
                {data.planChanges.length === 0 && (
                  <Typography sx={{ fontSize: 14, color: t.textMuted }}>No plan changes this period.</Typography>
                )}
                {data.planChanges.map((pc, i) => (
                  <Box key={i} sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "8px", paddingTop: "10px", borderTop: `1px solid ${t.borderMuted}` }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{pc.doctorName}</Typography>
                      <Typography sx={{ fontSize: 13, color: t.textMuted }}>{pc.what}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", alignItems: "flex-end" }}>
                      <EngagementPill t={t} tone={pc.kind === "Upgrade" ? "ok" : pc.kind === "Downgrade" ? "warn" : "info"}>{pc.kind}</EngagementPill>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        {pc.yearlyDiff === null ? "Not available" : `${pc.yearlyDiff >= 0 ? "+" : "-"}₹${Math.abs(pc.yearlyDiff).toLocaleString("en-IN")} a year`}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {(data.planChangesTotalPages || 1) > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", paddingTop: "10px" }}>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {data.planChangesPage || 1} of {data.planChangesTotalPages || 1}</Typography>
                    <Box sx={{ display: "flex", gap: "8px" }}>
                      <Box
                        component="button"
                        type="button"
                        disabled={planChangesPage <= 1}
                        onClick={() => setPlanChangesPage((p) => Math.max(1, p - 1))}
                        sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: planChangesPage <= 1 ? "not-allowed" : "pointer" }}
                      >
                        Previous
                      </Box>
                      <Box
                        component="button"
                        type="button"
                        disabled={planChangesPage >= (data.planChangesTotalPages || 1)}
                        onClick={() => setPlanChangesPage((p) => Math.min(data.planChangesTotalPages || 1, p + 1))}
                        sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: planChangesPage >= (data.planChangesTotalPages || 1) ? "not-allowed" : "pointer" }}
                      >
                        Next
                      </Box>
                    </Box>
                  </Box>
                )}
                {data.planChanges.length > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", paddingTop: "10px", borderTop: `1px solid ${t.border}`, fontSize: 15 }}>
                    <span>Net change in yearly value</span>
                    <strong style={{ color: data.netYearlyValue >= 0 ? t.tone.ok.color : t.tone.crit.color }}>
                      {data.netYearlyValue >= 0 ? "+" : "-"}₹{Math.abs(data.netYearlyValue).toLocaleString("en-IN")}
                    </strong>
                  </Box>
                )}
              </Box>

              <Box sx={{ flex: "1 1 320px", minWidth: 0, bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px", padding: "18px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Workload against the daily cap</Typography>
                {data.officers.map((o) => {
                  const pct = Math.min(100, Math.round((o.workload.avgPerDay / o.workload.cap) * 100));
                  return (
                    <Box key={o.id} sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: "8px", fontSize: 14 }}>
                        <Typography sx={{ fontWeight: 600 }}>{o.name}</Typography>
                        <Typography>{o.workload.avgPerDay} a day on average</Typography>
                      </Box>
                      <Box sx={{ height: 10, bgcolor: t.borderMuted, borderRadius: "5px" }}>
                        <Box sx={{ width: `${pct}%`, height: 10, borderRadius: "5px", bgcolor: o.workload.daysOverCap > 0 ? t.tone.crit.color : t.accent }} />
                      </Box>
                      <Typography sx={{ fontSize: 13, color: t.textMuted }}>
                        {o.workload.daysOverCap > 0
                          ? `Over the cap (${o.workload.cap}/day) on ${o.workload.daysOverCap} of ${o.workload.totalDays} days this period`
                          : `Within the cap every day this period (${o.workload.totalDays} days)`}
                      </Typography>
                    </Box>
                  );
                })}
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>
                  Tasks actually assigned to each officer per day, over the selected period — not a live queue size.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EngagementTeamOverview;
